import {
  ClockCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Row,
  Space,
  Statistic,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "../../general/table-components/table";
import styles from "./style.module.scss";

type FormatType = "number" | "minutes" | "percent";
type FieldErrors = Partial<Record<"startDate" | "endDate", string>>;

export interface IStatsMetric {
  labelKey: string;
  field: string;
  format?: FormatType;
  tooltipKey?: string;
}

export interface IStatsTableColumn {
  labelKey: string;
  field: string;
  format?: FormatType;
  width?: number;
}

export interface IStatsTableSection<TStats> {
  key: string;
  labelKey: string;
  tooltipKey?: string;
  rows: (stats: TStats) => Record<string, any>[];
  rowKey: (row: Record<string, any>, index: number) => string;
  columns: IStatsTableColumn[];
}

interface IProps<TStats extends { startDate: string; endDate: string }> {
  titleKey: string;
  averageLabelKey: string;
  averageField: string;
  includedField: string;
  includedLabelKey: string;
  excludedField?: string;
  excludedTooltipKey?: string;
  stats?: TStats;
  onChange: (stats: TStats) => void;
  loadStats?: (query: { startDate?: string; endDate?: string }) => Promise<{
    data: TStats;
  }>;
  hideDateControls?: boolean;
  metrics: IStatsMetric[];
  tables: IStatsTableSection<TStats>[];
}

const DATE_FORMAT = "YYYY-MM-DD";

const getValue = (item: Record<string, any> | undefined, field: string) =>
  field.split(".").reduce<any>((value, key) => value?.[key], item);

const extractFieldError = (error: any, field: "startDate" | "endDate") => {
  const errors = error?.response?.data?.errors;
  const value = errors?.[field];

  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  if (typeof value === "string") return value;

  return undefined;
};

const normalizeDatePickerValue = (value: string | string[]) =>
  Array.isArray(value) ? value[0] : value || undefined;

function OrderStatusStatsCard<TStats extends { startDate: string; endDate: string }>(
  props: IProps<TStats>,
) {
  const {
    stats,
    titleKey,
    averageLabelKey,
    averageField,
    includedField,
    includedLabelKey,
    excludedField,
    excludedTooltipKey,
    metrics,
    tables,
    loadStats,
    onChange,
    hideDateControls,
  } = props;
  const { t, i18n } = useTranslation();
  const [startDate, setStartDate] = useState<string | undefined>(
    stats?.startDate,
  );
  const [endDate, setEndDate] = useState<string | undefined>(stats?.endDate);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }),
    [i18n.language],
  );

  useEffect(() => {
    setStartDate(stats?.startDate);
    setEndDate(stats?.endDate);
  }, [stats?.endDate, stats?.startDate]);

  if (!stats) return null;

  const getNullableNumber = (value: unknown) =>
    typeof value === "number" && Number.isFinite(value) ? value : null;

  const formatValue = (value: unknown, format: FormatType = "number") => {
    const number = getNullableNumber(value);
    if (number === null) return "-";

    if (format === "minutes") {
      return `${numberFormatter.format(number)} ${t("minutes")}`;
    }

    if (format === "percent") {
      return `${numberFormatter.format(number)}%`;
    }

    return numberFormatter.format(number);
  };

  const disabledFutureDate = (date: Dayjs) =>
    date && date.isAfter(dayjs(), "day");

  const validateDates = () => {
    const errors: FieldErrors = {};
    const today = dayjs();
    const start = startDate ? dayjs(startDate, DATE_FORMAT, true) : null;
    const end = endDate ? dayjs(endDate, DATE_FORMAT, true) : null;

    if (startDate && (!start?.isValid() || start.isAfter(today, "day"))) {
      errors.startDate = t("invalid_date");
    }

    if (endDate && (!end?.isValid() || end.isAfter(today, "day"))) {
      errors.endDate = t("invalid_date");
    }

    if (start?.isValid() && end?.isValid() && end.isBefore(start, "day")) {
      errors.endDate = t("end_date_before_start_date");
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const refreshStats = async () => {
    if (!loadStats) return;
    if (!validateDates()) return;

    try {
      setLoading(true);
      setFieldErrors({});
      const { data } = await loadStats({ startDate, endDate });
      onChange(data);
      setStartDate(data.startDate);
      setEndDate(data.endDate);
    } catch (error) {
      setFieldErrors({
        startDate: extractFieldError(error, "startDate"),
        endDate: extractFieldError(error, "endDate"),
      });
    } finally {
      setLoading(false);
    }
  };

  const buildColumns = (
    columns: IStatsTableColumn[],
  ): ColumnsType<Record<string, any>> =>
    columns.map((column) => ({
      title: (
        <Tooltip title={t(column.labelKey)}>
          <span>{t(column.labelKey)}</span>
        </Tooltip>
      ),
      dataIndex: column.field,
      key: column.field,
      width: column.width,
      ellipsis: true,
      sorter: (a, b) => {
        const aValue = getValue(a, column.field);
        const bValue = getValue(b, column.field);

        if (typeof aValue === "number" && typeof bValue === "number") {
          return aValue - bValue;
        }

        return String(aValue ?? "").localeCompare(
          String(bValue ?? ""),
          i18n.language,
        );
      },
      render: (value: unknown) => {
        const displayValue = column.format
          ? formatValue(value, column.format)
          : value ?? "-";
        const displayText = String(displayValue);

        return (
          <Tooltip title={displayText}>
            <span className={styles.tableCell}>{displayText}</span>
          </Tooltip>
        );
      },
    }));

  const excludedCount =
    excludedField === undefined ? undefined : getValue(stats, excludedField);

  const renderStatsTable = (table: IStatsTableSection<TStats>) => (
    <Table
      rowKey={(row, index) => table.rowKey(row, index || 0)}
      columns={buildColumns(table.columns)}
      dataSource={table.rows(stats)}
      pagination={false}
      scroll={{ x: Math.max(760, table.columns.length * 150) }}
    />
  );

  return (
    <div className={styles.container}>
      <Divider orientation="center">{t(titleKey)}</Divider>
      <Card className={styles.panel} bodyStyle={{ padding: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} lg={hideDateControls || !loadStats ? 24 : 8}>
            <Statistic
              title={
                <Space>
                  <ClockCircleOutlined />
                  <span>{t(averageLabelKey)}</span>
                </Space>
              }
              value={formatValue(getValue(stats, averageField))}
              suffix={
                getNullableNumber(getValue(stats, averageField)) === null
                  ? undefined
                  : t("minutes")
              }
            />
            <Typography.Text type="secondary">
              {formatValue(getValue(stats, includedField))} {t(includedLabelKey)}
            </Typography.Text>
            <div className={styles.period}>
              {stats.startDate} - {stats.endDate}
              {typeof excludedCount === "number" && excludedCount > 0 && (
                <Tooltip
                  title={
                    excludedTooltipKey
                      ? t(excludedTooltipKey, { count: excludedCount })
                      : undefined
                  }
                >
                  <Tag color="warning" icon={<WarningOutlined />}>
                    {formatValue(excludedCount)}
                  </Tag>
                </Tooltip>
              )}
            </div>
          </Col>
          {!hideDateControls && loadStats && (
            <Col xs={24} lg={16}>
              <Row gutter={[12, 8]} align="top">
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={t("startDate")}
                    validateStatus={fieldErrors.startDate ? "error" : undefined}
                    help={fieldErrors.startDate}
                  >
                    <DatePicker
                      allowClear
                      disabledDate={disabledFutureDate}
                      format={DATE_FORMAT}
                      value={startDate ? dayjs(startDate) : undefined}
                      onChange={(_, value) =>
                        setStartDate(normalizeDatePickerValue(value))
                      }
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={t("endDate")}
                    validateStatus={fieldErrors.endDate ? "error" : undefined}
                    help={fieldErrors.endDate}
                  >
                    <DatePicker
                      allowClear
                      disabledDate={disabledFutureDate}
                      format={DATE_FORMAT}
                      value={endDate ? dayjs(endDate) : undefined}
                      onChange={(_, value) =>
                        setEndDate(normalizeDatePickerValue(value))
                      }
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    loading={loading}
                    onClick={refreshStats}
                    className={styles.refreshButton}
                  >
                    {t("refresh")}
                  </Button>
                </Col>
              </Row>
            </Col>
          )}
        </Row>

        <Row gutter={[12, 12]} className={styles.metrics}>
          {metrics.map((metric) => (
            <Col key={metric.field} xs={24} sm={12} lg={8} xl={6}>
              <Statistic
                title={
                  metric.tooltipKey ? (
                    <Tooltip title={t(metric.tooltipKey)}>
                      <span>{t(metric.labelKey)}</span>
                    </Tooltip>
                  ) : (
                    t(metric.labelKey)
                  )
                }
                value={formatValue(getValue(stats, metric.field), metric.format)}
              />
            </Col>
          ))}
        </Row>

        <div className={styles.tabs}>
          {tables.length === 1 ? (
            renderStatsTable(tables[0])
          ) : (
            <Tabs
              items={tables.map((table) => ({
                key: table.key,
                label: table.tooltipKey ? (
                  <Tooltip title={t(table.tooltipKey)}>
                    <Space size={4}>
                      <span>{t(table.labelKey)}</span>
                      <InfoCircleOutlined />
                    </Space>
                  </Tooltip>
                ) : (
                  t(table.labelKey)
                ),
                children: renderStatsTable(table),
              }))}
            />
          )}
        </div>
      </Card>
    </div>
  );
}

export default OrderStatusStatsCard;
